'use client';

import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { FC, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { Bounded } from '@/components/common/Bounded';

/**
 * Props for `ComparisonWithFeatureDiagram`.
 */
export type ComparisonWithFeatureDiagramProps =
  SliceComponentProps<Content.ComparisonWithFeatureDiagramSlice>;

/**
 * Component for "ComparisonWithFeatureDiagram" Slices.
 */
const ComparisonWithFeatureDiagram: FC<ComparisonWithFeatureDiagramProps> = ({
  slice,
}) => {
  const hasLottie = slice.primary.lottie === true;
  const assetLinkUrl =
    hasLottie && isFilled.link(slice.primary.asset_link)
      ? (slice.primary.asset_link.url ?? undefined)
      : undefined;
  const lottieSrc =
    assetLinkUrl && assetLinkUrl.toLowerCase().endsWith('.json')
      ? assetLinkUrl
      : undefined;
  const hasCenterpieceIcon = isFilled.image(slice.primary.centerpiece_icon);

  const [isLottieLoaded, setIsLottieLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Use Intersection Observer to detect when component is near viewport
  useEffect(() => {
    if (!lottieSrc || !sectionRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsInView(true);
        }
      },
      {
        rootMargin: '300px', // Start loading 300px before component enters viewport
        threshold: 0,
      },
    );

    observer.observe(sectionRef.current);

    return () => {
      observer.disconnect();
    };
  }, [lottieSrc]);

  // Only load Lottie when component is in view and after a delay
  useEffect(() => {
    if (!lottieSrc || !isInView) {
      return;
    }

    const loadLottie = () => {
      import('@lottiefiles/lottie-player').then(() => {
        setIsLottieLoaded(true);
      });
    };

    // Shorter delay on desktop, longer on mobile where resources are constrained
    const delay = isMobile ? 2000 : 600;
    const timeout = setTimeout(loadLottie, delay);
    return () => clearTimeout(timeout);
  }, [lottieSrc, isInView, isMobile]);

  // Create the lottie-player element dynamically once loaded
  useEffect(() => {
    if (isLottieLoaded && playerContainerRef.current && lottieSrc) {
      const container = playerContainerRef.current;
      const player = document.createElement('lottie-player');
      player.setAttribute('autoplay', '');
      player.setAttribute('loop', '');
      player.setAttribute('mode', 'normal');
      player.setAttribute('background', 'transparent');
      player.setAttribute('src', lottieSrc);
      player.style.width = '100%';
      player.style.height = 'auto';

      container.appendChild(player);

      return () => {
        if (container.contains(player)) {
          container.removeChild(player);
        }
      };
    }
  }, [isLottieLoaded, lottieSrc]);

  return (
    <section
      ref={sectionRef}
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-gradient-to-b from-[#0e1010] to-[#0f423a] text-white py-10 text-center"
    >
      <Bounded className="relative mx-auto max-w-screen-xl px-4 py-20">
        <div className="max-w-6xl mx-auto px-5">
          <div className="mb-[60px] text-left">
            <div className="mb-6">
              <PrismicRichText
                field={slice.primary.heading}
                components={{
                  heading1: ({ children }) => (
                    <h1 className="text-2xl md:text-3xl lg:text-4xl font-medium leading-[1.2] m-0 p-0">
                      {children}
                    </h1>
                  ),
                }}
              />
            </div>
            <div className="text-[#c4c4c4] text-lg leading-7">
              <PrismicRichText field={slice.primary.intro_text} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-[100px]">
            {/* Column One */}
            <div className="p-8 rounded-2xl text-left bg-black/20">
              <h2 className="text-2xl font-semibold mb-6 text-[#05f273]">
                {slice.primary.columnonetitle}
              </h2>
              <ul className="list-none p-0">
                {slice.primary.columnonebulletpoints?.map((item, idx) =>
                  item.text &&
                  typeof item.text === 'string' &&
                  item.text.trim() !== '' ? (
                    <li
                      key={idx}
                      className="mb-4 last:mb-0 flex items-start gap-2"
                    >
                      <span>{item.text}</span>
                    </li>
                  ) : null,
                )}
              </ul>
            </div>

            {/* Column Two */}
            <div className="p-8 rounded-2xl text-left bg-white/10">
              <h2 className="text-2xl font-semibold mb-6 text-[#05f273]">
                {slice.primary.columntwotitle}
              </h2>
              <ul className="list-none p-0">
                {slice.primary.columntwobulletpoints?.map((item, idx) =>
                  item.text &&
                  typeof item.text === 'string' &&
                  item.text.trim() !== '' ? (
                    <li
                      key={idx}
                      className="mb-4 last:mb-0 flex items-start gap-2"
                    >
                      <span>{item.text}</span>
                    </li>
                  ) : null,
                )}
              </ul>
            </div>
          </div>

          {/* Centerpiece Icon or Lottie Animation */}
          {lottieSrc ? (
            <div className="flex justify-center items-center my-4">
              <div
                ref={playerContainerRef}
                className="relative w-full max-w-[500px] min-h-[400px]"
              />
            </div>
          ) : hasCenterpieceIcon ? (
            <div className="flex justify-center items-center my-4">
              <div className="relative">
                <Image
                  src={slice.primary.centerpiece_icon.url!}
                  alt={slice.primary.centerpiece_icon.alt || ''}
                  width={500}
                  height={500}
                  className="w-auto h-auto max-w-full max-h-[400px] object-contain mx-auto"
                />
              </div>
            </div>
          ) : null}
        </div>
      </Bounded>
    </section>
  );
};

export default ComparisonWithFeatureDiagram;
